import { kebabCase } from "lodash";

import composeFieldName from "./composeFieldName";
import ucFirst from "../../utils/ucFirst";
import Facet, { FacetValue } from "../../types/funnelback/Facets";
import {
    SearchFieldCheckbox,
    SearchFieldRange,
    SearchFieldFacetType,
    SearchFieldOption,
    SearchFieldSelect,
    SearchFieldsType,
    SearchFieldMultiSelect,
} from "./SearchForm";

/*
 * Compose field value with multiple options
 */
const composeFieldOption = (
    rawCategoryValue: FacetValue,
    facetId: string
): SearchFieldOption => {
    const { data, label, queryStringParamValue } = rawCategoryValue;
    const id = `${facetId}-${kebabCase(data)}`;
    const option = {
        id,
        label: ucFirst(label),
        value: queryStringParamValue,
    };
    return option;
};

type FacetFieldProps = {
    [key: string]:
        | SearchFieldCheckbox
        | SearchFieldSelect
        | SearchFieldRange
        | SearchFieldMultiSelect;
};

/**
 * List of specific facets which should be displayed as a multi select dropdown
 * @see https://jira.squiz.net/browse/PLAN-224
 */
const multiSelectFacetNames = ["council", "location", "type"];

/*
 * Deserialise Funnelback facet category values to Formik values
 */
const composeFieldValues = (facets: Facet[]): SearchFieldsType | null => {
    const idPrefix = "planning-search-field-";
    if (!facets || !facets.length) {
        return {
            query: {
                name: "query",
                type: "text",
                label: "Keywords",
                id: `${idPrefix}query`,
                placeholder: "Search by keywords",
            },
        };
    }
    const facetFields: FacetFieldProps = {};
    facets
        .filter(
            (facet) =>
                facet.categories.length && facet.categories[0].values.length
        )
        .forEach((facet) => {
            const { categories, guessedDisplayType, name } = facet;
            const facetData = categories[0];
            const { queryStringParamName, values } = facetData;
            const fieldKey = composeFieldName(queryStringParamName);
            const id = `${idPrefix}${kebabCase(fieldKey)}`;

            // Assign default facet type
            let facetType: SearchFieldFacetType = "select";

            // Check if guessedDisplayType is set
            switch (guessedDisplayType) {
                case "CHECKBOX":
                    facetType = "checkboxGroup";
                    break;
                case "DATE_RANGE":
                    facetType = "dateRange";
            }

            // Check if facets should be displayed as a multi select component
            if (
                multiSelectFacetNames.includes(facet.name.toLowerCase()) &&
                facet.selectionType === "MULTIPLE"
            ) {
                facetType = "multiSelect";
            }

            const formattedField = {
                name: fieldKey,
                facetType,
                label: name,
                id,
                options: values.map((value) => composeFieldOption(value, id)),
            };
            facetFields[fieldKey] = formattedField;
        });
    const fieldsConfig = {
        query: {
            name: "query",
            type: "text",
            label: "Keywords",
            id: `${idPrefix}query`,
            placeholder: "Search by keywords",
        },
        ...facetFields,
    };
    return fieldsConfig;
};

export default composeFieldValues;
